import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Coffee, DollarSign, Camera, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

export function HomeownersSection() {
  const features = [
    {
      icon: <Coffee className="w-9 h-9" strokeWidth={2} />,
      title: 'Reclaim your mornings',
      description: 'Wake up. Sip coffee. Check app. Done. Your driveway\'s cleared while you\'re still in your bathrobe.',
      gradient: 'from-amber-500 to-orange-500',
    },
    {
      icon: <DollarSign className="w-9 h-9" strokeWidth={2} />,
      title: 'Zero pricing games',
      description: 'See the full cost breakdown before you tap "Book"—base rate, snow depth, add-ons. No hidden fees, ever.',
      gradient: 'from-accent-500 to-accent-600',
    },
    {
      icon: <Camera className="w-9 h-9" strokeWidth={2} />,
      title: 'Photo proof, always',
      description: 'Every job comes with timestamped before/after photos. Approve from anywhere or dispute if something\'s off.',
      gradient: 'from-brand-500 to-brand-600',
    },
  ];

  return (
    <Section
      id="homeowners"
      eyebrow="For Smart Homeowners"
      title="Stop wasting time on snow. Start living your life."
      subtitle="No more 5am wake-ups to shovel before work. No more hunting down the plow guy who 'usually' shows up. This is snow removal that actually respects your time."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card variant="frosted" className="h-full hover:shadow-card-hover transition-all duration-300 group border-2 border-transparent hover:border-brand-500/30">
              <motion.div 
                className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                whileHover={{ rotate: 5 }}
              >
                {feature.icon}
              </motion.div>
              <h3 className="font-black text-xl mb-3 text-neutral-900 group-hover:text-brand-500 transition-colors">
                {feature.title}
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                {feature.description}
              </p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Featured Testimonial */}
      <motion.div 
        className="mt-20 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <Card variant="solid" className="border-2 border-brand-500 bg-gradient-to-br from-brand-50 to-white shadow-glow relative overflow-hidden">
          {/* Decorative element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-500/5 rounded-full blur-3xl" />
          
          <div className="relative z-10 flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-black text-2xl flex-shrink-0 shadow-lg">
              JS
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div>
                  <p className="font-black text-xl text-neutral-900">John S.</p>
                  <p className="text-sm text-neutral-600 font-medium">Ottawa Homeowner • 2 Seasons</p>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.2, delay: 0.5 + (i * 0.1) }}
                      className="text-yellow-500 text-2xl"
                    >
                      ★
                    </motion.span>
                  ))}
                </div>
              </div>
              <p className="text-lg text-neutral-800 italic leading-relaxed font-medium mb-4">
                "I haven't touched a shovel in two winters. SNOWBLOWR just <strong className="text-brand-600 not-italic">works</strong>. Book at 6am, cleared by 8am, photos prove it. This is what every homeowner needs."
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-accent-500/10 text-accent-600 rounded-xl text-xs font-bold uppercase tracking-wide flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Verified User
                </span>
                <span className="px-4 py-2 bg-brand-500/10 text-brand-600 rounded-xl text-xs font-bold uppercase tracking-wide flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  24 Jobs Completed
                </span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </Section>
  );
}

